import { passwordProblems } from '../utils/passwordPolicy';
import PasswordChecklist from '../components/PasswordChecklist';
import React, { useState } from 'react';
import API from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import MFASetup from '../components/MFASetup';


const FirstLoginSetup: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState<'password' | 'mfa' | 'done'>('password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const problems = passwordProblems(newPassword);
    if (problems.length) {
      setError(`Password needs: ${problems.map(r => r.label.toLowerCase()).join(', ')}.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await API.put(`/api/users/${user?.id}/password`, { currentPassword, newPassword });
      // The server cleared the flag when the password was set; mirror it so PrivateRoute stops
      // redirecting here.
      updateUser({ mustChangePassword: false });

      setStep('mfa');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  const handleMFAComplete = () => {
    setStep('done');
  };

  const handleFinish = () => {
    // Reload to pick up the updated user state
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6 bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <img
              src="/ipri-logo.png"
              alt="IPRI"
              className="h-20 w-auto max-w-full object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Welcome to IPRI</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Please complete your account setup before continuing.
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step === 'password' ? 'text-emerald-600 font-medium' : step === 'mfa' || step === 'done' ? 'text-green-600' : 'text-gray-400 dark:text-gray-500'}`}>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${step === 'password' ? 'bg-emerald-600 text-white' : 'bg-green-600 text-white'}`}>
              {step === 'password' ? '1' : '\u2713'}
            </span>
            <span className="text-sm">Password</span>
          </div>
          <div className="w-8 h-px bg-gray-300 dark:bg-gray-600" />
          <div className={`flex items-center gap-2 ${step === 'mfa' ? 'text-emerald-600 font-medium' : step === 'done' ? 'text-green-600' : 'text-gray-400 dark:text-gray-500'}`}>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${step === 'mfa' ? 'bg-emerald-600 text-white' : step === 'done' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
              {step === 'done' ? '\u2713' : '2'}
            </span>
            <span className="text-sm">MFA Setup</span>
          </div>
        </div>

        {/* Step 1: Change Password */}
        {step === 'password' && (
          <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Change Your Password</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Your account was created with a temporary password. Please set a new secure password.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
                {error}
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Temporary Password</label>
                <input
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  placeholder="Enter your temporary password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm Password</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  placeholder="Confirm new password"
                />
              </div>
              <PasswordChecklist password={newPassword} />
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 font-medium"
              >
                {loading ? 'Updating...' : 'Set New Password'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: MFA Setup */}
        {step === 'mfa' && (
          <MFASetup
            onClose={handleMFAComplete}
            onSuccess={handleMFAComplete}
            allowClose={false}
          />
        )}

        {/* Done */}
        {step === 'done' && (
          <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Account Setup Complete</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Your password has been updated and two-factor authentication is enabled. You can now access the system.
            </p>
            <button
              onClick={handleFinish}
              className="w-full px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-medium"
            >
              Continue to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstLoginSetup;
